import { defer, firstValueFrom, from, lastValueFrom, Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap, switchMapTo, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { FILE_SYSTEM, IPC_CHANNEL } from '../_offline/electron.constants';
import { UtilsService } from './_utils.service';
import { ElectronService } from '../electron.service';

import UTILS = IPC_CHANNEL.UTILS;
import { isElectron } from '../_shared/environment-helper';

@Injectable({
  providedIn: 'root'
})
export class FileSystemAccessService {

  static PATH_SEPARATOR = '\\';

  private appFolderPath: string;
  private userFolderPath: string;

  constructor(
    private electronService: ElectronService,
    private utilsService: UtilsService
  ) {
    if (isElectron()) {
      this.initializeSystemPathSeparator()
        .then(separator => {
          if (separator) {
            FileSystemAccessService.PATH_SEPARATOR = separator;
          }
        });
    }
  }

  public async initializeSystemPathSeparator() {
    const pathSeparator = await firstValueFrom<string>(this.ipcSendSync(IPC_CHANNEL.FILE.GET_SYSTEM_PATH_DELIMITER));
    return pathSeparator;
  }

  async getFile(filePath: string, fileName: string, mimeType: string, isFullPath: boolean = false): Promise<File> {
    const fullPath = isFullPath ? filePath : await this.getFileFullPath(filePath);
    const base64File = await firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.GET_FILE, fullPath));
    return this.utilsService.convertFileFromBase64Str(base64File, fileName, mimeType);
  }

  private ipcInvoke(channel: string, ...args: string[]): Promise<string> {
    return this.electronService.invoke(channel, ...args);
  }

  public async readImageLockFile(): Promise<string> {
    return await this.ipcInvoke(IPC_CHANNEL.BRIAN.READ_LOCK_IMAGE_FILE);
  }

 public async readImagePrefix(): Promise<string> {
    return await this.ipcInvoke(IPC_CHANNEL.BRIAN.READ_IMAGE_PREFIX);
  }

  public async updateImagePrefix(content: string): Promise<string> {
    return await this.ipcInvoke(IPC_CHANNEL.BRIAN.UPDATE_IMAGE_PREFIX, content);
  }

  public async updateImageLockFile(imageLock: boolean): Promise<string> {
    const binaryLock = imageLock ? 1 : 0;
    const lockContent = JSON.stringify({ lock: binaryLock });
    return await this.ipcInvoke(IPC_CHANNEL.BRIAN.UPDATE_IMAGE_LOCK_FILE, lockContent);
  }

  async readFile(filePath: string): Promise<string> {
    return this.getFileFullPath(filePath)
      .then(fullPath => firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.READ_TEXT_FILE, fullPath)));
  }

  async saveFile(file: File, filePath: string): Promise<boolean> {
    return Promise.all([
      this.getFileFullPath(filePath),
      this.utilsService.fileToBase64(file)
    ])
      .then(values => {
        const fullPath = values[0];
        const base64 = values[1];
        return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.PUT_ONE, fullPath, base64, file.type));
      });
  }

  async createTextFile(context: string, savePath: string): Promise<boolean> {
    return this.getFileFullPath(savePath)
      .then(fullPath =>
        firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.POST_TEXT_FILE, fullPath, context))
      );
  }

  async renameFilePath(oldFilePath: string, newFilePath: string): Promise<boolean> {
    return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.PUT_FILE_PATH, oldFilePath, newFilePath));
  }

  async createFolder(filePath: string, isAppData = true): Promise<boolean> {
    if (isAppData) {
      // 指定パスを /ESS/ViSOL直下に作成
      const fullPath = await this.getFileFullPath(filePath);
      return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.CREATE_FOLDER, fullPath));
    } else {
      // 指定されたパスのフォルダを作成
      return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.CREATE_FOLDER, filePath));
    }

  }

  async deleteFile(filePath: string): Promise<boolean> {
    const fullPath = await this.getFileFullPath(filePath);
    return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.DELETE_ONE, fullPath));
  }

  /**
   * ファイルを削除する。パスが存在しない場合の例外は無視する。
   *
   * @param filePath ファイルパス。
   * @returns 成功した場合は true を返す。
   */
  public async deleteFileForce(filePath: string): Promise<boolean> {
    const fullPath: string = await this.getFileFullPath(filePath);
    return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.DELETE_FILE_FORCE, fullPath));
  }

  async deleteFolder(filePath: string, isFullPath: boolean = false) {
    const fullPath = isFullPath ? filePath : await this.getFileFullPath(filePath);
    return firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.DELETE_FOLDER, fullPath));
  }

  public async getSaveBlobFolderPath(): Promise<string> {
    const appFolderPath = await this.getAppFolderPath();
    return [appFolderPath, FILE_SYSTEM.PATH.BLOBS].join(FileSystemAccessService.PATH_SEPARATOR) + FileSystemAccessService.PATH_SEPARATOR;
  }

  public async getAppFolderPath() {
    if (!this.appFolderPath) {
      this.appFolderPath = await firstValueFrom(this.ipcSendSync(IPC_CHANNEL.FILE.GET_APP_FOLDER_PATH));
    }

    return this.appFolderPath;
  }

  public async getUserFolderPath() {
    if (!this.userFolderPath) {
      this.userFolderPath = await this.ipcSendSync(IPC_CHANNEL.FILE.GET_USER_PATH)
        .toPromise();
    }

    return this.userFolderPath;
  }

  public async getAppFolderParentPath() {
    const appFolderPath = await this.getAppFolderPath();
    const pathParts = this.appFolderPath.split(FileSystemAccessService.PATH_SEPARATOR);
    pathParts.pop();
    return pathParts.join(FileSystemAccessService.PATH_SEPARATOR);
  }

  /**
   * ファイルが存在するか確認する。
   *
   * @param filePath ファイルパス。
   * @returns 存在する場合は true を返す。
   */
  public isFileExists(filePath: string, isViSOLAppFolder = true): Observable<boolean> {
    if (!isViSOLAppFolder) {
      return this.ipcSendSync(IPC_CHANNEL.FILE.IS_FILE_EXISTS, filePath) as Observable<boolean>;
    }

    return defer(() => this.getFileFullPath(filePath))
      .pipe(
        mergeMap(fullPath => this.ipcSendSync(IPC_CHANNEL.FILE.IS_FILE_EXISTS, fullPath)),
        map(x => x as boolean)
      );
  }

  private ipcSendSync(channel: string, ...args: any[]) {
    const sendSyncOf = of(this.electronService.sendSyncCommand(channel, ...args));
    return timer(50)
      .pipe(switchMapTo(sendSyncOf))
      .pipe(take(1))
      .pipe(catchError((error: any) => throwError(() => error.json)));
  }

  public async getFileFullPath(filePath: string): Promise<string> {
    const basePath = await this.getSaveBlobFolderPath();
    return [basePath, filePath].join("");
  }

  public openExplorer(folderPath: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.FILE.OPEN_EXPLORER, folderPath);
  }

  public async getSaveIplexOneFolderPath(): Promise<string> {
    const appFolderPath = await this.getAppFolderParentPath();
    return [appFolderPath, FILE_SYSTEM.PATH.IPLEX_ONE].join(FileSystemAccessService.PATH_SEPARATOR);
  }

  public getIPLEXOneFolderPathForBrian(): string {
    return [FILE_SYSTEM.PATH.SYSTEM_DRIVE, FILE_SYSTEM.PATH.IPLEX_ONE_RETURN_APP].join(FileSystemAccessService.PATH_SEPARATOR);
  }

  public executeReturnAppBatFile(): Observable<boolean> {
  return from(this.getReturnAppFilePath()).pipe(
    switchMap(filePath =>
      this.isFileExists(filePath, false).pipe(
        map(fileExists => {
          if (fileExists) {
            this.ipcSendSync(UTILS.EXECUTE_BAT_FILE, filePath);
            return true;
          }
          return false;
        })
      )
    )
  );
  }

  private async getReturnAppFilePath(): Promise<string> {
    return [this.getIPLEXOneFolderPathForBrian(), FILE_SYSTEM.FILE_NAME.RETURN_APP_BAT_FILE].join(FileSystemAccessService.PATH_SEPARATOR);
  }

  public getSystemPathSeparator() {
    return FileSystemAccessService.PATH_SEPARATOR;
  }

}
